// Utility functions to map Airtable data to the format our app expects

// Map Airtable project record to our app format
export const mapAirtableProject = (record) => {
  if (!record || !record.fields) return null;
  
  // Log to debug
  console.log('Raw project record:', record);
  
  // Handle status mapping carefully - preserve spaces, normalize case
  let status = record.fields.Status || '';
  
  // Map Airtable status values to our app's format
  switch(status.trim()) {
    case 'On Track':
      status = 'on-track';
      break;
    case 'Delayed':
      status = 'delayed';
      break;
    case 'At Risk':
      status = 'at-risk';
      break;
    default:
      // Convert to lowercase and replace spaces with hyphens for other values
      status = status.toLowerCase().replace(/\s+/g, '-');
  }
  
  const mapped = {
    id: record.id,
    name: record.fields.Name || '',
    status: status,
    dueDate: record.fields['Due Date'] || '',
    priority: (record.fields.Priority || '').toLowerCase()
  };
  
  console.log('Mapped project:', mapped);
  return mapped;
};

// Map Airtable department record to our app format
export const mapAirtableDepartment = (record) => {
  if (!record || !record.fields) return null;
  
  // Determine icon based on department name if not explicitly set
  let icon = record.fields.Icon || '';
  if (!icon) {
    // Default icon mapping based on department name
    const deptName = record.fields.Name || '';
    
    if (deptName.includes('Pre-Production')) icon = 'PenTool';
    else if (deptName.includes('Model')) icon = 'Cube';
    else if (deptName.includes('Rigg')) icon = 'Users';
    else if (deptName.includes('Animation')) icon = 'Film';
    else if (deptName.includes('Light') || deptName.includes('Render')) icon = 'Zap';
    else if (deptName.includes('Post')) icon = 'Edit';
    else icon = 'PenTool'; // Default
  }
  
  return {
    id: record.id,
    name: record.fields.Name || '',
    icon: icon,
    description: record.fields.Description || ''
  };
};

// Map Airtable team member record to our app format
export const mapAirtableTeamMember = (record) => {
  if (!record || !record.fields) return null;
  
  return {
    id: record.id,
    name: record.fields.Name || '',
    role: record.fields.Role || '',
    email: record.fields.Email || ''
  };
};

// Find a team member's name by ID
export const getTeamMemberName = (teamId, teamMembers) => {
  if (!teamId || !teamMembers || !teamMembers.length) return 'Unassigned';
  
  // Handle if teamId is an array (which is how Airtable stores linked records)
  const id = Array.isArray(teamId) ? teamId[0] : teamId;
  
  const member = teamMembers.find(m => m.id === id);
  return member?.fields?.Name || 'Unassigned';
};

// Find a department's name by ID
export const getDepartmentName = (deptId, departments) => {
  if (!deptId || !departments || !departments.length) return '';
  
  const dept = departments.find(d => d.id === deptId);
  return dept?.fields?.Name || '';
};

// Map Airtable task record to our app format
export const mapAirtableTask = (record, departments, teamMembers) => {
  if (!record || !record.fields) return null;
  
  // Map status properly
  let status = record.fields.Status || '';
  
  // Handle specific status values from Airtable
  switch(status.trim()) {
    case 'Not Started':
      status = 'not-started';
      break;
    case 'In Progress':
      status = 'in-progress';
      break;
    case 'Completed':
    case 'Complete':
      status = 'completed';
      break;
    default:
      // Convert to lowercase and replace spaces with hyphens for other values
      status = status.toLowerCase().replace(/\s+/g, '-');
  }
  
  return {
    id: record.id,
    name: record.fields.Name || '',
    status: status,
    progress: Number(record.fields.Progress) || 0,
    assignee: getTeamMemberName(record.fields.Team, teamMembers), // Changed from "Assignee" to "Team"
    due: record.fields['Due Date'] || '',
    department: getDepartmentName(record.fields.Department, departments),
    departmentId: record.fields.Department || '',
    projectId: record.fields.Project || '',
    assigneeId: record.fields.Team || '' // Changed from "Assignee" to "Team"
  };
};

// Prepare a task for Airtable (reverse mapping)
export const prepareTaskForAirtable = (taskData) => {
  // Map status back to Airtable format
  let status = '';
  switch(taskData.status) {
    case 'not-started':
      status = 'Not Started';
      break;
    case 'in-progress':
      status = 'In Progress';
      break;
    case 'completed':
      status = 'Complete';
      break;
    default:
      status = taskData.status
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
  }
  
  return {
    Name: taskData.name,
    Status: status,
    Progress: parseInt(taskData.progress, 10),
    Department: [taskData.department], // Convert to array
    Project: [taskData.project],       // Convert to array
    Team: [taskData.assignee],         // Changed from "Assignee" to "Team"
    'Due Date': taskData.dueDate
  };
};

export default {
  mapAirtableProject,
  mapAirtableDepartment,
  mapAirtableTeamMember,
  mapAirtableTask,
  prepareTaskForAirtable,
  getTeamMemberName,
  getDepartmentName
};